<template>
    <div>
        <app-bar :title="t('app_structures_title')"></app-bar>

        <v-container fluid class="px-6 pt-6 pb-8">
            <v-alert
                type="info"
                text
                dense
                class="mb-6"
            >{{ t('app_structures_text_intro') }}</v-alert>

            <v-card
                elevation="0"
                rounded="lg"
            >
                <v-card-title class="px-6 pt-5 pb-3">
                    <v-switch
                        v-model="unpublished"
                        inset
                        hide-details
                        class="mt-0 pt-0"
                        :label="t('app_structures_label_unpublished_only')"
                        @change="capture"
                    ></v-switch>

                    <v-spacer></v-spacer>

                    <publish-app-structure-modal @success="capture">
                        <template #default="{ on, attrs }">
                            <v-btn
                                color="primary"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon left>mdi-upload</v-icon>
                                {{ t('app_structures_action_publish') }}
                            </v-btn>
                        </template>
                    </publish-app-structure-modal>
                </v-card-title>

                <v-card-text class="px-6 pb-4">
                    <v-data-table
                        :headers="headers"
                        :items="items"
                        :loading="busy"
                        item-key="version_hash"
                        :items-per-page="50"
                        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
                        :no-data-text="t('general_text_no_data')"
                    >
                        <template #item.version_hash="{ item }">
                            <code :title="item.version_hash">{{ short(item.version_hash) }}</code>
                            <v-btn
                                icon
                                x-small
                                class="ml-1"
                                :title="t('app_structures_action_copy_hash')"
                                @click="copy(item.version_hash)"
                            >
                                <v-icon x-small>mdi-content-copy</v-icon>
                            </v-btn>
                        </template>

                        <template #item.platform="{ item }">{{ item.platform || '-' }}</template>

                        <template #item.app_version="{ item }">{{ item.app_version || '-' }}</template>

                        <template #item.published="{ item }">
                            <v-chip
                                small
                                dark
                                :color="item.published ? 'success' : 'error'"
                            >{{ item.published ? t('app_structures_text_published') : t('app_structures_text_missing') }}</v-chip>
                        </template>

                        <template #item.published_at="{ item }">{{ date(item.published_at) }}</template>

                        <template #item.first_seen_at="{ item }">
                            {{ item.first_seen_at ? date(item.first_seen_at) : t('app_structures_text_not_seen') }}
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-container>
    </div>
</template>

<script lang="ts">
    import { defineComponent, reactive, computed, onMounted, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import moment from 'moment-timezone'
    import { useStore } from '@/store'
    import { useAppStructureHttp } from '@/core/api/app-structures'
    import PublishAppStructureModal from '@/components/app-structures/PublishAppStructureModal.vue'

    /**
     * SSO-3534 — the maps of the app, one row per map rather than per build: builds whose map did
     * not change send the same hash and share a row.
     *
     * A row can exist without a map. That is the case worth looking at: a session opened on a
     * build whose map nobody published, which the backend records at the first chat and serves an
     * older map for until someone publishes the right one here.
     */
    export default defineComponent({
        components: {
            PublishAppStructureModal,
        },

        setup() {
            const store = useStore()
            const i18n = useI18n()

            const state = reactive({
                busy: true,
                unpublished: false,
                items: <any[]>[],
            })

            const headers = computed(() => [
                { text: i18n.t('app_structures_label_hash'), value: 'version_hash', sortable: false },
                { text: i18n.t('app_structures_label_platform'), value: 'platform' },
                { text: i18n.t('app_structures_label_app_version'), value: 'app_version' },
                { text: i18n.t('app_structures_label_status'), value: 'published' },
                { text: i18n.t('app_structures_label_published_at'), value: 'published_at' },
                { text: i18n.t('app_structures_label_first_seen_at'), value: 'first_seen_at' },
                { text: i18n.t('app_structures_label_sessions_seen'), value: 'sessions_seen', align: 'end' },
            ])

            const capture = () => {
                state.busy = true
                useAppStructureHttp()
                    .get<any>(state.unpublished ? { unpublished: 1 } : {})
                    .then((response: any) => {
                        state.items = response.data
                    })
                    .finally(() => {
                        state.busy = false
                    })
            }

            // `sha256:` plus 64 hex characters is too long for a table cell; the full value is
            // in the tooltip and one click from the clipboard.
            const short = (hash: string): string => {
                return hash && hash.length > 22 ? `${hash.slice(0, 15)}…${hash.slice(-4)}` : hash
            }

            const date = (value?: string): string => {
                return value ? moment(value).format('YYYY-MM-DD HH:mm') : '-'
            }

            const copy = (hash: string) => {
                navigator.clipboard.writeText(hash).then(() => {
                    store.dispatch('notify', { color: 'success', message: i18n.t('app_structures_notice_copied') })
                })
            }

            onMounted(() => {
                capture()
            })

            return {
                headers,
                capture,
                short,
                date,
                copy,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
